/**
 * Final scores. SPEC.md §52, §32.1, §32.2, §31.
 *
 * Ranked by score descending, with seat order a tap away for anyone comparing
 * against the physical table. Signs are always explicit, and the received/paid
 * line plus its tick is the proof that the game balanced. §52, §19
 *
 * Nothing here mutates the finished game: an ended game is read-only. "New Game"
 * creates a fresh session rather than resetting this one, so the history survives.
 * §32.1, §32.2
 */

import { useMemo, useState } from 'react';
import {
  balanceTotals,
  formatBalanceCompact,
  formatJoinCode,
  formatSigned,
  messageForErrorCode,
  truncateName,
  type PlayerView,
} from '@card-counter/shared';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { SignedScore } from '@/components/ui/SignedScore';
import { createSession, errorMessageOf } from '@/lib/api';
import { writePlayerToken } from '@/lib/storage';
import { navigate, paths } from '@/router';
import {
  useAllPlayersBySeat,
  useMe,
  useSessionInfo,
  useSessionStore,
} from '@/stores/session-store';
import { useUiStore } from '@/stores/ui-store';

type Order = 'score' | 'seat';

interface Row {
  player: PlayerView;
  total: number;
  /** Only meaningful in score order; equal scores share a place. §52 */
  rank: number | null;
}

export function FinalScoreScreen() {
  const session = useSessionInfo();
  // Removed players keep their place in the final accounting. §31
  const players = useAllPlayersBySeat();
  const totals = useSessionStore((state) => state.totalsByPlayerId);
  const me = useMe();
  const openHistory = useUiStore((state) => state.openHistory);

  // A view preference, local to this screen and nobody else's business. §37
  const [order, setOrder] = useState<Order>('score');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const rows = useMemo<Row[]>(() => {
    const scored = players.map((player) => ({ player, total: totals[player.id] ?? 0 }));
    if (order === 'seat') {
      return scored.map((row) => ({ ...row, rank: null }));
    }
    // Seat order breaks ties so the list is stable between renders. §9.1
    const ranked = [...scored].sort(
      (a, b) => b.total - a.total || a.player.seatIndex - b.player.seatIndex,
    );
    let rank = 0;
    let previous: number | null = null;
    return ranked.map((row, index) => {
      if (previous === null || row.total !== previous) {
        rank = index + 1;
        previous = row.total;
      }
      return { ...row, rank };
    });
  }, [players, totals, order]);

  const balance = balanceTotals(totals);

  async function startNewGame(): Promise<void> {
    // Reuses this device's identity: the name and avatar are already known, so a
    // new table is one tap rather than another form. §6.3, §32.2
    if (!me) {
      navigate('/?create');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const credentials = await createSession({
        name: me.name,
        avatarKey: me.avatarKey,
        // The same night carries on, so the same rules do — a table that has
        // been playing for drinks does not silently become an ordinary one
        // because somebody tapped New Game. §4.6, §32.2
        ...(session?.drinkingMode && session.pointsPerDrink !== null
          ? { drinkingMode: true, pointsPerDrink: session.pointsPerDrink }
          : {}),
      });
      writePlayerToken(credentials.sessionId, credentials.playerToken);
      navigate(paths.game(credentials.sessionId));
    } catch (err) {
      setCreateError(errorMessageOf(err));
      setCreating(false);
    }
  }

  return (
    <main className="flex min-h-0 flex-1 flex-col">
      <div className="scroll-area flex-1">
        <div className="mx-auto flex w-full max-w-sm flex-col gap-5 p-safe">
          <header className="flex flex-col items-center gap-1 text-center">
            <h1 className="text-sm font-semibold tracking-[0.2em] text-ink-faint uppercase">
              Final Score
            </h1>
            {session ? (
              <p data-numeric className="text-xl font-bold tracking-[0.15em] text-ink">
                {formatJoinCode(session.joinCode)}
              </p>
            ) : null}
            {/* §32.1 — the game is finished and read-only. */}
            <p className="text-sm text-ink-muted">{messageForErrorCode('SESSION_ENDED')}</p>
          </header>

          <div className="flex justify-center gap-2" role="group" aria-label="Order">
            <Button
              size="sm"
              variant="secondary"
              selected={order === 'score'}
              onClick={() => {
                setOrder('score');
              }}
            >
              By score
            </Button>
            <Button
              size="sm"
              variant="secondary"
              selected={order === 'seat'}
              onClick={() => {
                setOrder('seat');
              }}
            >
              By seat
            </Button>
          </div>

          <ol className="flex flex-col gap-2">
            {rows.map(({ player, total, rank }) => {
              return (
                <li
                  key={player.id}
                  className={[
                    'flex min-h-tap items-center gap-3 rounded-card border bg-surface px-3 py-2',
                    player.id === me?.id ? 'border-accent' : 'border-line',
                  ].join(' ')}
                >
                  <span
                    data-numeric
                    aria-hidden={rank === null}
                    className="w-5 shrink-0 text-sm text-ink-faint"
                  >
                    {rank === null ? '' : rank}
                  </span>
                  <Avatar avatarKey={player.avatarKey} className="size-8 shrink-0" />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-ink">{truncateName(player.name)}</span>
                    {player.isRemoved ? (
                      // Kept visible because their points are part of the total. §31
                      <span className="text-xs text-ink-faint">Left the table</span>
                    ) : null}
                  </span>
                  {player.id === me?.id ? (
                    <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-semibold tracking-wide text-accent uppercase">
                      You
                    </span>
                  ) : null}
                  <SignedScore value={total} className="text-score font-bold" />
                </li>
              );
            })}
          </ol>

          {/*
            The proof that the night balanced — and the one game where there is
            no such thing. A drinking table destroys points on every hand it
            settles and creates them on every drink, so the received/paid pair
            would carry a permanent ⚠ for behaving exactly as designed. It says
            what the points *were* instead. §52, §4.6, §14.3
          */}
          <div className="flex flex-col items-center gap-1 rounded-card border border-line bg-surface px-3 py-3 text-center">
            <p className="text-xs font-semibold tracking-[0.2em] text-ink-faint uppercase">Total</p>
            {session?.drinkingMode ? (
              <p data-numeric className="text-lg font-semibold text-ink">
                {formatSigned(balance.positiveTotal + balance.negativeTotal)} on the table
              </p>
            ) : (
              <>
                <p data-numeric className="text-lg font-semibold text-ink">
                  {formatBalanceCompact(balance.positiveTotal, balance.negativeTotal)}
                </p>
                {balance.balanced ? null : (
                  // Never ask a player to balance the game by hand. §14.3
                  <p role="status" className="text-sm text-warn">
                    Still syncing with the server.
                  </p>
                )}
              </>
            )}
          </div>

          {createError !== null ? (
            <p role="alert" className="text-center text-sm text-negative">
              {createError}
            </p>
          ) : null}

          <div className="flex flex-col gap-2">
            <Button
              size="lg"
              fullWidth
              loading={creating}
              onClick={() => {
                void startNewGame();
              }}
            >
              New Game
            </Button>
            <Button variant="secondary" fullWidth onClick={openHistory}>
              View History
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
}
